/**
 * 360 AR Viewer - Dynamic Face-to-Mesh Pipeline
 * 
 * Pipeline for EVERY new generated frontal baby image:
 * 1. Extract face-only region (eyes, nose, mouth, chin, cheeks)
 * 2. Create anatomical face mask (exclude scalp, ears, neck, body)
 * 3. Detect mesh landmarks (nose tip, eye sockets, mouth area)
 * 4. Map face identity regions onto mesh with zone-based blending
 * 5. Validate at multiple angles (front, +/-15, +/-30)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Button } from '../../components/ui/button';
import { ArrowLeft, RotateCcw, Loader2, Eye, ChevronLeft, ChevronRight } from 'lucide-react';

/* ==================== FACE PROJECTION SHADER ==================== */

const faceProjectionVertexShader = `
  varying vec3 vNormal;
  varying vec3 vLocalPos;
  varying vec3 vWorldNormal;
  varying vec3 vViewDir;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vLocalPos = position;
    vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);

    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vViewDir = normalize(cameraPosition - worldPos.xyz);

    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Zone-aware fragment shader:
// - Central face (eyes/nose/mouth/chin): texture driven
// - Forehead: mesh-driven above eyebrow line
// - Ears/side: mesh-driven (facingFront falloff)
// - Scalp/top: pure mesh skin
// - Eyes: reduced raw texture, use for eyelid shape + tone
const faceProjectionFragmentShader = `
  uniform sampler2D faceTexture;
  uniform vec3 skinColor;
  uniform vec3 skinColorDark;
  uniform vec3 lightDir;
  uniform vec3 lightDir2;
  uniform float projScale;
  uniform vec2 projOffset;
  uniform float blendSharpness;
  uniform float noseTipY;
  uniform float meshFaceTop;
  uniform float meshFaceBot;

  varying vec3 vNormal;
  varying vec3 vLocalPos;
  varying vec3 vWorldNormal;
  varying vec3 vViewDir;

  void main() {
    // Project face texture from front (Z-axis)
    vec2 projUV = vec2(
      vLocalPos.x * projScale + projOffset.x,
      vLocalPos.y * projScale + projOffset.y
    );

    // UV range check
    float inRange = step(0.0, projUV.x) * step(projUV.x, 1.0) * step(0.0, projUV.y) * step(projUV.y, 1.0);

    // Sample face texture (alpha from elliptical mask controls face region)
    vec4 faceColor = texture2D(faceTexture, projUV);

    // PRIMARY BLEND: texture alpha from face mask (elliptical region)
    float blend = faceColor.a * inRange;

    // Only show on front-facing surfaces (prevent wrap-around)
    // Use view direction instead of fixed Z-axis so face stays aligned when model rotates
    float facingFront = dot(normalize(vWorldNormal), normalize(vViewDir));
    float dirBlend = smoothstep(0.05, 0.45, facingFront);
    blend *= dirBlend;

    // Skip very dark background pixels
    float brightness = max(faceColor.r, max(faceColor.g, faceColor.b));
    float contentMask = smoothstep(0.05, 0.14, brightness);
    blend *= contentMask;

    // Skip pure white/gray (Polaroid remnants)
    float minC = min(faceColor.r, min(faceColor.g, faceColor.b));
    float sat = (brightness - minC) / (brightness + 0.001);
    float whiteFilter = 1.0 - step(0.82, brightness) * step(sat, 0.10);
    blend *= whiteFilter;

    // Upper skull fade: reduce texture near top of head (mesh-driven scalp)
    float eyebrowLine = noseTipY + (meshFaceTop - noseTipY) * 0.60;
    float scalpFade = 1.0 - smoothstep(eyebrowLine, meshFaceTop * 0.92, vLocalPos.y);
    blend *= scalpFade;

    // Skin base with subtle variation
    float skinVar = 0.97 + 0.03 * sin(vLocalPos.x * 5.0 + vLocalPos.y * 3.0);
    vec3 baseSkin = mix(skinColorDark, skinColor, skinVar);

    // Tint face texture to match skin tone for seamless transition
    vec3 tintedFace = mix(faceColor.rgb, faceColor.rgb * vec3(1.03, 0.97, 0.92), 0.12);

    // Final blend: face texture in identity zones, mesh skin everywhere else
    vec3 color = mix(baseSkin, tintedFace, clamp(blend, 0.0, 1.0));

    // Lighting
    float diff1 = max(dot(normalize(vNormal), normalize(lightDir)), 0.0);
    float diff2 = max(dot(normalize(vNormal), normalize(lightDir2)), 0.0);
    float ambient = 0.35;
    float lighting = ambient + 0.45 * diff1 + 0.2 * diff2;

    // FACE ZONE: Nearly flat lighting to prevent geometry shadows (eye sockets, nostrils)
    float faceLighting = 0.85 + 0.10 * diff1 + 0.05 * diff2; // Very high ambient, minimal directional
    lighting = mix(lighting, faceLighting, clamp(blend, 0.0, 1.0));

    // Rim light (stronger on sides for 3D depth)
    float rim = 1.0 - max(dot(normalize(vNormal), normalize(vViewDir)), 0.0);
    rim = pow(rim, 3.0) * 0.18;

    // Subsurface scattering
    float sss = max(dot(normalize(-vNormal), normalize(lightDir)), 0.0);
    sss = pow(sss, 2.0) * 0.08;

    color *= lighting;
    color += vec3(1.0, 0.85, 0.75) * rim;
    color += vec3(1.0, 0.7, 0.6) * sss;

    // Subtle warm tint
    color = mix(color, color * vec3(1.02, 0.98, 0.95), 0.3);

    gl_FragColor = vec4(color, 1.0);
  }
`;

/* ==================== FACE EXTRACTION PIPELINE ==================== */

/**
 * Extract face-only region from a generated baby image.
 * Creates an elliptical mask covering only:
 * - Eye area (eyelid shape + surrounding tone)
 * - Nose (bridge to tip)
 * - Mouth and chin center
 * - Front cheeks
 * 
 * Excludes: scalp, ears, neck, shoulders, background
 */
function extractFaceRegion(faceImg) {
  // Step 1: Draw full image to temp canvas
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = faceImg.width;
  tempCanvas.height = faceImg.height;
  const tempCtx = tempCanvas.getContext('2d');
  tempCtx.drawImage(faceImg, 0, 0);

  // Step 2: Crop Polaroid frame
  const cropL = Math.floor(faceImg.width * 0.08);
  const cropR = Math.floor(faceImg.width * 0.08);
  const cropT = Math.floor(faceImg.height * 0.09);
  const cropB = Math.floor(faceImg.height * 0.21);
  const innerW = faceImg.width - cropL - cropR;
  const innerH = faceImg.height - cropT - cropB;

  // Step 3: Find face bounding box (excluding dark + white pixels)
  const innerData = tempCtx.getImageData(cropL, cropT, innerW, innerH);
  const px = innerData.data;
  let fMinX = innerW, fMaxX = 0, fMinY = innerH, fMaxY = 0;
  let sumX = 0, sumY = 0, sumW = 0;

  for (let y = 0; y < innerH; y++) {
    for (let x = 0; x < innerW; x++) {
      const i = (y * innerW + x) * 4;
      const brightness = Math.max(px[i], px[i + 1], px[i + 2]);
      if (brightness > 45 && brightness < 220) {
        if (x < fMinX) fMinX = x;
        if (x > fMaxX) fMaxX = x;
        if (y < fMinY) fMinY = y;
        if (y > fMaxY) fMaxY = y;
        sumX += x * brightness;
        sumY += y * brightness;
        sumW += brightness;
      }
    }
  }

  const faceCX = sumW > 0 ? sumX / sumW : innerW / 2;
  const faceCY = sumW > 0 ? sumY / sumW : innerH / 2;
  const faceW = fMaxX - fMinX;
  const faceH = fMaxY - fMinY;

  // Add small padding
  const pad = Math.floor(Math.max(faceW, faceH) * 0.02);
  fMinX = Math.max(0, fMinX - pad);
  fMinY = Math.max(0, fMinY - pad);
  fMaxX = Math.min(innerW - 1, fMaxX + pad);
  fMaxY = Math.min(innerH - 1, fMaxY + pad);
  const finalW = fMaxX - fMinX;
  const finalH = fMaxY - fMinY;

  // Step 4: Estimate anatomical landmarks (ratios within the face bounding box)
  const eyeLineY = fMinY + faceH * 0.30;     // Eyebrow level
  const noseLineY = fMinY + faceH * 0.55;    // Nose tip
  const mouthLineY = fMinY + faceH * 0.70;   // Mouth center
  const chinLineY = fMinY + faceH * 0.92;    // Chin bottom

  // Face mask center: between eyes and mouth (the core identity zone)
  const maskCenterX = faceCX;
  const maskCenterY = (noseLineY + eyeLineY) / 2; // Centered between eyes and nose

  // Ellipse radii for face-only region
  const ellipseRX = faceW * 0.48;  // ~96% face width (inner cheeks, exclude ears)
  const ellipseRY = faceH * 0.42;  // From eyebrows to chin area

  // Step 5: Draw cropped face to 1024x1024 texture canvas
  const faceCanvas = document.createElement('canvas');
  faceCanvas.width = 1024;
  faceCanvas.height = 1024;
  const fCtx = faceCanvas.getContext('2d');
  fCtx.clearRect(0, 0, 1024, 1024);

  // Draw face centered, filling canvas
  const faceAspect = finalW / finalH;
  let dw, dh;
  if (faceAspect > 1) {
    dw = 1024;
    dh = 1024 / faceAspect;
  } else {
    dh = 1024;
    dw = 1024 * faceAspect;
  }
  const ddx = (1024 - dw) / 2;
  const ddy = (1024 - dh) / 2;

  fCtx.drawImage(
    tempCanvas,
    cropL + fMinX, cropT + fMinY, finalW, finalH,
    ddx, ddy, dw, dh
  );

  // Step 6: Create elliptical face mask (the core of face-only extraction)
  // Map anatomical landmarks from inner-image coords to canvas coords
  const scaleToCanvas = dw / finalW;
  const canvasMaskCX = ddx + (maskCenterX - fMinX) * scaleToCanvas;
  const canvasMaskCY = ddy + (maskCenterY - fMinY) * scaleToCanvas;
  const canvasEllipseRX = ellipseRX * scaleToCanvas;
  const canvasEllipseRY = ellipseRY * scaleToCanvas;

  // Create mask canvas with soft elliptical gradient
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = 1024;
  maskCanvas.height = 1024;
  const mCtx = maskCanvas.getContext('2d');

  // Draw soft elliptical gradient mask
  mCtx.save();
  mCtx.translate(canvasMaskCX, canvasMaskCY);
  // Scale Y to create ellipse from circular gradient
  const scaleRatio = canvasEllipseRY / canvasEllipseRX;
  mCtx.scale(1, scaleRatio);
  const grad = mCtx.createRadialGradient(0, 0, 0, 0, 0, canvasEllipseRX);
  // Core face zone: full opacity
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.50, 'rgba(255,255,255,1)');
  // Transition zone: smooth falloff
  grad.addColorStop(0.70, 'rgba(255,255,255,0.75)');
  grad.addColorStop(0.85, 'rgba(255,255,255,0.3)');
  grad.addColorStop(0.95, 'rgba(255,255,255,0.05)');
  grad.addColorStop(1.0, 'rgba(255,255,255,0)');
  mCtx.fillStyle = grad;
  // Fill area that covers the ellipse (in scaled space)
  const fillSize = canvasEllipseRX * 1.2;
  mCtx.fillRect(-fillSize, -fillSize / scaleRatio, fillSize * 2, (fillSize * 2) / scaleRatio);
  mCtx.restore();

  // Apply mask to face texture (multiply alpha)
  fCtx.globalCompositeOperation = 'destination-in';
  fCtx.drawImage(maskCanvas, 0, 0);
  fCtx.globalCompositeOperation = 'source-over';

  // Note: Source image is already bright (avg ~210/255). Dark areas on mesh come from
  // 3D geometry shadows, not from image pixels. Shader handles this with flat lighting.
  // Note: Source image brightness is adequate. No normalization needed.

  // Step 7: Compute image analysis data
  const centroidInCropX = faceCX - fMinX;
  const centroidInCropY = faceCY - fMinY;
  const imgFaceCenterU = (ddx + (centroidInCropX / finalW) * dw) / 1024;
  const imgFaceCenterV = (ddy + (centroidInCropY / finalH) * dh) / 1024;
  const imgFaceH_UV = dh / 1024;
  const imgFaceW_UV = dw / 1024;

  // Nose position in UV (for anchor alignment)
  const noseInCropY = noseLineY - fMinY;
  const imgNoseV = (ddy + (noseInCropY / finalH) * dh) / 1024;

  console.log('Face extraction:', {
    faceBBox: [fMinX, fMinY, fMaxX, fMaxY],
    centroidUV: [imgFaceCenterU.toFixed(3), imgFaceCenterV.toFixed(3)],
    noseV: imgNoseV.toFixed(3),
    maskCenter: [canvasMaskCX.toFixed(0), canvasMaskCY.toFixed(0)],
    ellipseR: [canvasEllipseRX.toFixed(0), canvasEllipseRY.toFixed(0)]
  });

  return {
    canvas: faceCanvas,
    maskCanvas: maskCanvas,
    analysis: {
      centerU: imgFaceCenterU,
      centerV: imgFaceCenterV,
      noseV: imgNoseV,
      faceH: imgFaceH_UV,
      faceW: imgFaceW_UV,
      // Anatomical ratios (for shader zone control)
      eyeLineRatio: 0.30,
      noseLineRatio: 0.55,
      mouthLineRatio: 0.70,
      chinLineRatio: 0.92
    }
  };
}

/* ==================== MESH ANALYSIS ==================== */

function analyzeMeshGeometry(geo) {
  const positions = geo.attributes.position;
  const normals = geo.attributes.normal;
  const vertCount = positions.count;

  let meshFaceMinY = Infinity, meshFaceMaxY = -Infinity;
  let meshFaceMinX = Infinity, meshFaceMaxX = -Infinity;
  let noseTipZ = -Infinity, noseTipY = 0, noseTipX = 0;
  let meshFaceSumY = 0, meshFaceSumX = 0, meshFaceW = 0;

  // Also find eye socket candidates (concave areas near nose level)
  let eyeSocketCandidatesY = [];

  for (let i = 0; i < vertCount; i++) {
    const nz = normals.getZ(i);
    const vx = positions.getX(i);
    const vy = positions.getY(i);
    const vz = positions.getZ(i);

    // Front-facing vertices (normal Z > 0.3)
    if (nz > 0.3) {
      if (vy < meshFaceMinY) meshFaceMinY = vy;
      if (vy > meshFaceMaxY) meshFaceMaxY = vy;
      if (vx < meshFaceMinX) meshFaceMinX = vx;
      if (vx > meshFaceMaxX) meshFaceMaxX = vx;

      const w = nz;
      meshFaceSumY += vy * w;
      meshFaceSumX += vx * w;
      meshFaceW += w;

      // Nose tip = most forward Z vertex
      if (vz > noseTipZ) {
        noseTipZ = vz;
        noseTipY = vy;
        noseTipX = vx;
      }

      // Eye sockets: vertices that are concave (slightly recessed) above nose
      if (vy > noseTipY && nz > 0.4 && nz < 0.8 && Math.abs(vx) > 0.1) {
        eyeSocketCandidatesY.push(vy);
      }
    }
  }

  const meshFaceCenterX = meshFaceW > 0 ? meshFaceSumX / meshFaceW : 0;
  const meshFaceCenterY = meshFaceW > 0 ? meshFaceSumY / meshFaceW : 0;
  const meshFaceHeight = meshFaceMaxY - meshFaceMinY;
  const meshFaceWidth = meshFaceMaxX - meshFaceMinX;

  // Estimate eye level from socket candidates
  let eyeLevel = noseTipY + meshFaceHeight * 0.15; // Default
  if (eyeSocketCandidatesY.length > 5) {
    eyeSocketCandidatesY.sort((a, b) => a - b);
    const mid = Math.floor(eyeSocketCandidatesY.length / 2);
    eyeLevel = eyeSocketCandidatesY[mid];
  }

  console.log('Mesh analysis:', {
    faceCenterLocal: [meshFaceCenterX.toFixed(3), meshFaceCenterY.toFixed(3)],
    faceHeight: meshFaceHeight.toFixed(3),
    faceWidth: meshFaceWidth.toFixed(3),
    noseTip: [noseTipX.toFixed(3), noseTipY.toFixed(3), noseTipZ.toFixed(3)],
    eyeLevel: eyeLevel.toFixed(3),
    faceYRange: [meshFaceMinY.toFixed(3), meshFaceMaxY.toFixed(3)]
  });

  return {
    faceCenterX: meshFaceCenterX,
    faceCenterY: meshFaceCenterY,
    faceHeight: meshFaceHeight,
    faceWidth: meshFaceWidth,
    noseTipY,
    noseTipZ,
    eyeLevel,
    faceMinY: meshFaceMinY,
    faceMaxY: meshFaceMaxY
  };
}

/* ==================== AUTO ALIGNMENT ==================== */

function computeAlignment(imageAnalysis, meshData) {
  // Scale: match image face height to mesh face height
  const scaleFromHeight = imageAnalysis.faceH / meshData.faceHeight;
  const scaleFromWidth = imageAnalysis.faceW / meshData.faceWidth;
  const projScale = scaleFromHeight * 0.6 + scaleFromWidth * 0.4;

  // X offset: align horizontal centers
  const offX = imageAnalysis.centerU - meshData.faceCenterX * projScale;

  // Y offset: anchor on nose tip (most reliable anatomical landmark)
  // The image nose position (in UV) should map to the mesh nose Y
  const offY = imageAnalysis.noseV - meshData.noseTipY * projScale;

  console.log('Auto-alignment:', {
    projScale: projScale.toFixed(4),
    projOffset: [offX.toFixed(4), offY.toFixed(4)],
    imgNoseV: imageAnalysis.noseV.toFixed(3),
    meshNoseY: meshData.noseTipY.toFixed(3)
  });

  return { projScale, offX, offY };
}

/* ==================== MAIN PAGE ==================== */

const AR360ViewPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sceneReady, setSceneReady] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [currentAngle, setCurrentAngle] = useState('front');

  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animationRef = useRef(null);
  const modelGroupRef = useRef(null);

  // Camera angle presets for validation
  const anglePresets = {
    'front': { x: 0, y: 0.3, z: 4.0, label: 'On' },
    '15L': { x: -1.05, y: 0.3, z: 3.86, label: '15° Sol' },
    '15R': { x: 1.05, y: 0.3, z: 3.86, label: '15° Sag' },
    '30L': { x: -2.0, y: 0.3, z: 3.46, label: '30° Sol' },
    '30R': { x: 2.0, y: 0.3, z: 3.46, label: '30° Sag' },
  };

  const setValidationAngle = useCallback((angleKey) => {
    const preset = anglePresets[angleKey];
    if (!preset || !cameraRef.current || !controlsRef.current) return;

    setCurrentAngle(angleKey);
    const camera = cameraRef.current;
    const controls = controlsRef.current;

    // Animate camera to preset position
    const startPos = camera.position.clone();
    const endPos = new THREE.Vector3(preset.x, preset.y, preset.z);
    const duration = 500;
    const startTime = Date.now();

    function animateCamera() {
      const elapsed = Date.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const ease = t * (2 - t); // ease out quad

      camera.position.lerpVectors(startPos, endPos, ease);
      controls.update();

      if (t < 1) requestAnimationFrame(animateCamera);
    }
    animateCamera();
  }, []);

  useEffect(() => {
    const storedImage = sessionStorage.getItem('ar_360_image');
    const urlImage = searchParams.get('image');
    if (storedImage) {
      setImageUrl(storedImage);
    } else if (urlImage) {
      setImageUrl(decodeURIComponent(urlImage));
    }
    setLoading(false);
  }, [searchParams]);

  const initScene = useCallback((container) => {
    if (!container || !imageUrl) return;

    if (rendererRef.current) {
      rendererRef.current.dispose();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    }

    setModelLoading(true);
    const w = container.clientWidth;
    const h = container.clientHeight;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = true;
    container.innerHTML = '';
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#080810');
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 100);
    camera.position.set(0, 0.3, 4.0);
    cameraRef.current = camera;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;
    controls.minDistance = 2.0;
    controls.maxDistance = 7;
    controls.minPolarAngle = Math.PI * 0.15;
    controls.maxPolarAngle = Math.PI * 0.8;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, 0, 0);
    controlsRef.current = controls;

    // Studio Lighting
    const ambient = new THREE.AmbientLight(0xfff5ee, 0.3);
    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0xfff5ee, 1.0);
    keyLight.position.set(3, 6, 5);
    keyLight.castShadow = true;
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xe0d8f0, 0.4);
    fillLight.position.set(-4, 4, -2);
    scene.add(fillLight);

    const rimLight = new THREE.PointLight(0xffeedd, 0.3);
    rimLight.position.set(0, -1, 4);
    scene.add(rimLight);

    const backLight = new THREE.PointLight(0xccddff, 0.2);
    backLight.position.set(2, 2, -5);
    scene.add(backLight);

    // Ground shadow
    const groundGeo = new THREE.PlaneGeometry(12, 12);
    const groundMat = new THREE.ShadowMaterial({ opacity: 0.25 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -1.5;
    ground.receiveShadow = true;
    scene.add(ground);

    // Subtle grid
    const gridHelper = new THREE.GridHelper(6, 20, 0x1a1a2e, 0x1a1a2e);
    gridHelper.position.y = -1.5;
    gridHelper.material.opacity = 0.15;
    gridHelper.material.transparent = true;
    scene.add(gridHelper);

    // === DYNAMIC FACE EXTRACTION PIPELINE ===
    const faceImg = new Image();
    if (imageUrl.startsWith('http')) {
      faceImg.crossOrigin = 'anonymous';
    }

    faceImg.onload = () => {
      // Extract face-only region with elliptical mask
      const extraction = extractFaceRegion(faceImg);

      const faceTexture = new THREE.CanvasTexture(extraction.canvas);
      faceTexture.colorSpace = THREE.SRGBColorSpace;
      faceTexture.minFilter = THREE.LinearFilter;
      faceTexture.magFilter = THREE.LinearFilter;

      loadFetalHead(scene, faceTexture, extraction.analysis);
    };

    faceImg.onerror = () => {
      loadFetalHead(scene, null, null);
    };
    faceImg.src = imageUrl;

    function loadFetalHead(sc, faceTex, imageAnalysis) {
      const loader = new GLTFLoader();
      loader.load(
        '/models/fetal_head.glb',
        (gltf) => {
          const model = gltf.scene;

          const box = new THREE.Box3().setFromObject(model);
          const size = box.getSize(new THREE.Vector3());
          const center = box.getCenter(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = maxDim > 0 ? 2.2 / maxDim : 1;
          model.scale.setScalar(scale);
          model.position.set(-center.x * scale, -center.y * scale, -center.z * scale);

          model.traverse((child) => {
            if (child.isMesh && child.geometry) {
              child.geometry.computeVertexNormals();
              const geo = child.geometry;
              geo.computeBoundingBox();
              const bb = geo.boundingBox;
              const sizeX = bb.max.x - bb.min.x;
              const sizeY = bb.max.y - bb.min.y;
              const centerX = (bb.max.x + bb.min.x) / 2;
              const centerY = (bb.max.y + bb.min.y) / 2;

              // Analyze mesh geometry for face landmarks
              const meshData = analyzeMeshGeometry(geo);

              // Compute auto-alignment
              let calcProjScale, calcOffX, calcOffY;

              if (faceTex && imageAnalysis) {
                const alignment = computeAlignment(imageAnalysis, meshData);
                calcProjScale = alignment.projScale;
                calcOffX = alignment.offX;
                calcOffY = alignment.offY;
              } else {
                calcProjScale = 1.0 / Math.max(sizeX, sizeY) * 0.55;
                calcOffX = 0.5 - centerX * calcProjScale;
                calcOffY = 0.5 - centerY * calcProjScale;
              }

              if (faceTex) {
                child.material = new THREE.ShaderMaterial({
                  uniforms: {
                    faceTexture: { value: faceTex },
                    skinColor: { value: new THREE.Color(0.88, 0.65, 0.50) },
                    skinColorDark: { value: new THREE.Color(0.78, 0.55, 0.40) },
                    lightDir: { value: new THREE.Vector3(3, 6, 5).normalize() },
                    lightDir2: { value: new THREE.Vector3(-4, 4, -2).normalize() },
                    projScale: { value: calcProjScale },
                    projOffset: { value: new THREE.Vector2(calcOffX, calcOffY) },
                    blendSharpness: { value: 0.55 },
                    noseTipY: { value: meshData.noseTipY },
                    meshFaceTop: { value: meshData.faceMaxY },
                    meshFaceBot: { value: meshData.faceMinY }
                  },
                  vertexShader: faceProjectionVertexShader,
                  fragmentShader: faceProjectionFragmentShader,
                  side: THREE.DoubleSide
                });
              } else {
                child.material = new THREE.MeshPhysicalMaterial({
                  color: new THREE.Color(0.95, 0.80, 0.72),
                  roughness: 0.55,
                  metalness: 0,
                  clearcoat: 0.1,
                  sheen: 0.3,
                  sheenColor: new THREE.Color(0.9, 0.7, 0.65),
                  side: THREE.DoubleSide
                });
              }

              child.castShadow = true;
            }
          });

          sc.add(model);
          modelGroupRef.current = model;
          setSceneReady(true);
          setModelLoading(false);
        },
        undefined,
        (error) => {
          console.error('Model yukleme hatasi:', error);
          setModelLoading(false);
        }
      );
    }

    // Animation loop (subtle idle movement only)
    const clock = new THREE.Clock();
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize
    const onResize = () => {
      const nw = container.clientWidth;
      const nh = container.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      controls.dispose();
      renderer.dispose();
    };
  }, [imageUrl]);

  useEffect(() => {
    if (containerRef.current && imageUrl) {
      const cleanup = initScene(containerRef.current);
      return cleanup;
    }
  }, [imageUrl, initScene]);

  const handleReset = () => {
    if (controlsRef.current && cameraRef.current) {
      setValidationAngle('front');
    }
  };

  // Drag to rotate handler
  const handleDrag = useCallback((e) => {
    if (!controlsRef.current || !modelGroupRef.current) return;
    const touch = e.touches ? e.touches[0] : e;
    const x = touch.clientX;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const relX = (x - rect.left) / rect.width;
    modelGroupRef.current.rotation.y = (relX - 0.5) * Math.PI * 0.5;
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080810] flex items-center justify-center" data-testid="ar360-loading">
        <Loader2 className="w-8 h-8 animate-spin text-rose-400" />
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div className="min-h-screen bg-[#080810] flex flex-col items-center justify-center gap-4 p-4" data-testid="ar360-no-image">
        <Eye className="w-12 h-12 text-gray-500" />
        <p className="text-gray-400 text-center">Goruntu bulunamadi. Once Ultrasound 4K ile bir bebek gorseli olusturun.</p>
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="ar360-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Geri Don
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080810] flex flex-col" data-testid="ar360-viewer">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800/50" data-testid="ar360-header">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="ar360-nav-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <span className="text-sm font-medium text-rose-300">360° Bebek Gorunumu</span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleReset} data-testid="ar360-reset-btn">
          <RotateCcw className="w-4 h-4 mr-1" /> Sifirla
        </Button>
      </div>

      {/* 3D Viewer */}
      <div className="flex-1 relative">
        <div
          ref={containerRef}
          className="w-full h-full"
          style={{ minHeight: 'calc(100vh - 130px)' }}
          data-testid="ar360-canvas-container"
        />

        {modelLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50" data-testid="ar360-model-loading">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-rose-400" />
              <span className="text-sm text-gray-300">Yuz-mesh hizalama yapiliyor...</span>
            </div>
          </div>
        )}
      </div>

      {/* Validation Angle Controls */}
      {sceneReady && (
        <div className="px-4 py-3 border-t border-gray-800/50 bg-gray-900/50" data-testid="ar360-angle-controls">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 mr-2">Dogrulama Acisi:</span>
            {Object.entries(anglePresets).map(([key, preset]) => (
              <Button
                key={key}
                variant={currentAngle === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setValidationAngle(key)}
                className={`text-xs px-3 py-1 ${currentAngle === key ? 'bg-rose-500/80 text-white' : 'text-gray-400 border-gray-700'}`}
                data-testid={`ar360-angle-${key}`}
              >
                {key.includes('L') && <ChevronLeft className="w-3 h-3 mr-0.5" />}
                {preset.label}
                {key.includes('R') && <ChevronRight className="w-3 h-3 ml-0.5" />}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AR360ViewPage;
